import React, { useEffect } from 'react'
import { Text, View } from 'react-native'
import styled from 'styled-components/native'
import { Feather } from '../../../components/icon/vectorIcons'
import Modal from '../../../components/common/Modal'
import Button from '../../../components/common/Button'
import useTranslation from '../../../hooks/useTranslation'
import { EditorPerson } from '../api'
import { RemoteSave, SaveStatus } from './useDocletAutosave'
import { builderColors } from './builderColors'

const statusIcon: Record<
  SaveStatus,
  React.ComponentProps<typeof Feather>['name']
> = {
  saved: 'check',
  pending: 'edit-2',
  saving: 'upload-cloud',
  error: 'alert-circle',
  conflict: 'alert-triangle',
  deleted: 'trash-2'
}

/** "Saved" / "Saving…" / "Couldn't save" beside the header buttons. */
export const SaveStatusIndicator = ({ status }: { status: SaveStatus }) => {
  const { t } = useTranslation()
  const isProblem =
    status === 'error' || status === 'conflict' || status === 'deleted'
  const color = isProblem ? builderColors.danger : builderColors.mutedText
  return (
    <StatusRow accessibilityLiveRegion="polite">
      <Feather name={statusIcon[status]} size={14} color={color} />
      <StatusText style={{ color }}>
        {t(`doclets:saveStatus:${status}`)}
      </StatusText>
    </StatusRow>
  )
}

const fullName = (person: EditorPerson) =>
  [person.firstName, person.lastName].filter(Boolean).join(' ')

interface ConflictModalProps {
  /** The other editor's save; null when there's no conflict. */
  conflict: RemoteSave | null
  /** Called when the save's author isn't known yet, to look them up. */
  onNeedName: () => void
  onReload: () => void
  onOverwrite: () => void
}

/** Someone else saved over the draft this editor was working from. */
export const DraftConflictModal = ({
  conflict,
  onNeedName,
  onReload,
  onOverwrite
}: ConflictModalProps) => {
  const { t } = useTranslation()
  const open = !!conflict
  const needsName = open && !conflict?.by

  // A save rejected by the API doesn't say who saved over it; look it up
  useEffect(() => {
    if (needsName) onNeedName()
  }, [needsName, onNeedName])

  const name = conflict?.by ? fullName(conflict.by) : null
  return (
    <Modal
      open={open}
      close={() => {}}
      bodyStyle={{ padding: 24, maxWidth: 480 }}
    >
      <ModalTitle>
        {name
          ? t('doclets:conflict:titleNamed', { name })
          : t('doclets:conflict:title')}
      </ModalTitle>
      <ModalText>{t('doclets:conflict:body')}</ModalText>
      <ButtonRow>
        <Button
          label={t('doclets:conflict:overwrite')}
          onPress={onOverwrite}
          buttonStyle={{
            backgroundColor: '#FFFFFF',
            borderWidth: 1,
            borderColor: builderColors.border
          }}
          titleStyle={{ color: builderColors.text }}
        />
        <Button
          label={t('doclets:conflict:reload')}
          onPress={onReload}
          style={{ marginLeft: 8 }}
        />
      </ButtonRow>
    </Modal>
  )
}

const StatusRow = styled(View)`
  flex-direction: row;
  align-items: center;
  margin-right: 16px;
`

const StatusText = styled(Text)`
  font-size: 13px;
  margin-left: 6px;
`

const ModalTitle = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  color: ${builderColors.text};
  margin-bottom: 8px;
`

const ModalText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  color: ${builderColors.text};
  margin-bottom: 20px;
`

const ButtonRow = styled(View)`
  flex-direction: row;
  justify-content: flex-end;
`
